import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, CostSummaryResponse, publishProject, unpublishProject, getPublicationStatus, getPublishedSnapshot, getPublicationVersions, getPublicationVersion, getPublicationLive, clonePublishedProject, PublicationStatus, PublishedSnapshot, VersionListItem, VersionDetail, LiveSnapshot } from './client'
import { useAuth } from '../contexts/AuthContext'
import { useAnalytics } from '../contexts/AnalyticsContext'
import { AnalyticsEvents } from '../utils/analytics-events'
import type { Project, ProjectListItem, PaginatedResponse, ProjectFormat, ProjectStatus } from './types'

// Query keys
export const queryKeys = {
  projects: ['projects'] as const,
  project: (id: string) => ['project', id] as const,
  sparks: ['sparks'] as const,
  pricing: ['pricing'] as const,
  costSummary: (projectId: string) => ['costSummary', projectId] as const,
  publicationStatus: (projectId: string) => ['publicationStatus', projectId] as const,
  publishedSnapshot: (key: string) => ['publishedSnapshot', key] as const,
  publicationVersions: (projectId: string) => ['publicationVersions', projectId] as const,
  publicationVersion: (projectId: string, versionNumber: number) => ['publicationVersion', projectId, versionNumber] as const,
  publicationLive: (projectId: string) => ['publicationLive', projectId] as const,
}
// Projects
export function useProjects(limit = 50, offset = 0) {
  return useQuery<PaginatedResponse<ProjectListItem>>({
    queryKey: [...queryKeys.projects, { limit, offset }],
    queryFn: () => api.listProjects(limit, offset),
  })
}

// Sparks (credits balance)
const _isLocalDev = import.meta.env.DEV &&
  !import.meta.env.VITE_ENABLE_AUTH &&
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

export function useSparks() {
  const { isAuthenticated } = useAuth()
  return useQuery<{ balance: number }>({
    queryKey: queryKeys.sparks,
    queryFn: () => api.getSparks(),
    enabled: isAuthenticated || _isLocalDev,
    staleTime: 30_000,
  })
}

export function useAddSparks() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sparks, idempotencyKey }: { sparks: number; idempotencyKey: string }) => {
      return api.addSparks(sparks, idempotencyKey)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparks })
    },
  })
}

export function useCostSummary(projectId: string | undefined) {
  return useQuery<CostSummaryResponse>({
    queryKey: queryKeys.costSummary(projectId!),
    queryFn: () => api.getCostSummary(projectId!),
    enabled: !!projectId,
  })
}

export function useProject(projectId: string) {
  return useQuery<Project>({
    queryKey: queryKeys.project(projectId),
    queryFn: () => api.getProject(projectId),
    enabled: !!projectId,
  })
}

// Create project
export function useCreateProject() {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: async ({
      prompt,
      format = 'webtoon',
      name,
    }: {
      prompt: string
      format?: ProjectFormat
      name?: string
    }) => {
      return api.createProject(prompt, format, name)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      track(AnalyticsEvents.PROJECT_CREATED, { format: variables.format || 'webtoon' })
    },
  })
}

// Delete project
export function useDeleteProject() {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: async (projectId: string) => {
      return api.deleteProject(projectId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      track(AnalyticsEvents.PROJECT_DELETED)
    },
  })
}

// Update project
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string
      data: { name?: string; status?: ProjectStatus; has_celebrity?: boolean }
    }) => {
      return api.updateProject(projectId, data)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.project(variables.projectId) })
      const previousProject = queryClient.getQueryData(queryKeys.project(variables.projectId))
      queryClient.setQueryData(queryKeys.project(variables.projectId), (old: Project | undefined) =>
        old ? { ...old, ...variables.data } : old
      )
      return { previousProject }
    },
    onError: (_err, variables, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(queryKeys.project(variables.projectId), context.previousProject)
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      queryClient.invalidateQueries({ queryKey: queryKeys.project(variables.projectId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Publication hooks (RFC-0295 — Snapshot-based sharing)
// ---------------------------------------------------------------------------

export function usePublicationStatus(projectId: string | undefined) {
  return useQuery<PublicationStatus>({
    queryKey: queryKeys.publicationStatus(projectId!),
    queryFn: () => getPublicationStatus(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })
}

export function usePublishProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => publishProject(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.publicationStatus(projectId) })
    },
  })
}

export function useUnpublishProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => unpublishProject(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.publicationStatus(projectId) })
    },
  })
}

export function usePublishedSnapshot(projectToken: string | undefined, versionSlug: string | undefined) {
  return useQuery<PublishedSnapshot>({
    queryKey: queryKeys.publishedSnapshot(`${projectToken}/${versionSlug}`),
    queryFn: () => getPublishedSnapshot(projectToken!, versionSlug!),
    enabled: !!projectToken && !!versionSlug,
  })
}

export function usePublicationVersions(projectId: string | undefined) {
  return useQuery<VersionListItem[]>({
    queryKey: queryKeys.publicationVersions(projectId!),
    queryFn: () => getPublicationVersions(projectId!),
    enabled: !!projectId,
  })
}

export function usePublicationVersion(projectId: string | undefined, versionNumber: number | undefined) {
  return useQuery<VersionDetail>({
    queryKey: queryKeys.publicationVersion(projectId!, versionNumber!),
    queryFn: () => getPublicationVersion(projectId!, versionNumber!),
    enabled: !!projectId && versionNumber !== undefined,
  })
}

export function usePublicationLive(projectId: string | undefined) {
  return useQuery<LiveSnapshot>({
    queryKey: queryKeys.publicationLive(projectId!),
    queryFn: () => getPublicationLive(projectId!),
    enabled: !!projectId,
  })
}

export function useClonePublishedProject() {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: ({ projectToken, versionSlug }: { projectToken: string; versionSlug: string }) =>
      clonePublishedProject(projectToken, versionSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      track(AnalyticsEvents.PROJECT_CLONED)
    },
  })
}
